/**
 * Tempo di tiro: **una casella sola**, in secondi, con la virgola o il punto.
 *
 * La versione precedente aveva due caselle — secondi e centesimi — con il
 * salto automatico di fuoco alla seconda cifra. Sembrava comodo e non lo
 * era: dopo il salto la casella dei centesimi mostrava `00` non selezionato,
 * digitare `8` dava `008` → `00`, e su iPhone un `type="number"` non accetta
 * la virgola della tastiera italiana. Al test: «non riesco a mettere due
 * cifre». Troppi meccanismi per un numero.
 *
 * Qui c'è un campo di testo con tastiera decimale, una bozza locale mentre
 * ha il fuoco e la normalizzazione **al blur**: `4,82`, `4.82` e `4.8` sono
 * tutti leggibili, e il campo mostra sempre due decimali come un cronometro.
 * Il parsing è in `shootingPlan.js`, puro e testato.
 */
import { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { formatShotTime, parseShotTime } from "./shootingPlan";

export function TimeField({
	value,
	onChange,
	autoFocus = false,
	compact = false,
	placeholder = "0.00",
	ariaLabel = "Tempo in secondi",
}) {
	// `null` = nessuna bozza: si mostra il valore normalizzato.
	const [draft, setDraft] = useState(null);

	// Il valore cambia da fuori (si ricarica un risultato salvato): la bozza
	// va buttata, o il campo resta fermo su quello che si stava scrivendo.
	useEffect(() => {
		setDraft(null);
	}, [value]);

	const shown = draft ?? formatShotTime(value);

	const commit = () => {
		if (draft === null) return;
		const parsed = parseShotTime(draft);
		setDraft(null);
		const next = parsed === null ? "" : String(parsed);
		if (next !== String(value ?? "")) onChange(next);
	};

	return (
		<div className={`relative ${compact ? "w-full" : "flex-1"}`}>
			<Input
				type="text"
				inputMode="decimal"
				autoComplete="off"
				autoFocus={autoFocus}
				value={shown}
				onFocus={(e) => e.target.select()}
				onChange={(e) => setDraft(e.target.value.replace(/[^\d.,]/g, ""))}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") e.currentTarget.blur();
				}}
				className={`bg-background border-transparent rounded-sm h-11 text-base tabular-nums ${
					compact ? "px-2 pr-6" : "px-3 pr-7"
				}`}
				placeholder={placeholder}
				aria-label={ariaLabel}
			/>
			<span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
				s
			</span>
		</div>
	);
}
